import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Check, X, ChevronDown, ChevronUp } from "lucide-react";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function addDays(iso, n) {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d;
}
function pretty(d) {
  return d.toLocaleDateString("en-US", { day: "numeric", month: "short" });
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function AdminApprovals() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [projects, setProjects] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        api.get("/projects"),
        api.get("/timesheets/pending"),
      ]);
      const map = {};
      pRes.data.forEach((p) => (map[p.id] = p.name));
      setProjects(map);
      setItems(tRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const approve = async (id) => {
    try {
      await api.post(`/timesheets/${id}/approve`);
      toast.success("Timesheet approved");
      setItems((prev) => prev.filter((t) => t.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const reject = async () => {
    if (!rejectReason.trim()) {
      toast.error("Add a reason before rejecting.");
      return;
    }
    try {
      await api.post(`/timesheets/${rejectTarget.id}/reject`, { reason: rejectReason });
      toast.success("Timesheet rejected");
      setItems((prev) => prev.filter((t) => t.id !== rejectTarget.id));
      setRejectTarget(null);
      setRejectReason("");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-[1400px]">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">
            Pending approvals
          </div>
          <h1 className="font-display font-black text-3xl md:text-5xl leading-none">
            Review the queue.
          </h1>
        </div>
        <div className="text-sm text-neutral-500" data-testid="pending-count">
          {items.length} awaiting
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E5] rounded-md p-12 text-center text-neutral-500">
          {loading ? "Loading…" : "Nothing to review. All caught up."}
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((t) => {
            const isOpen = expanded === t.id;
            const dayTotals = Array.from({ length: 7 }, (_, i) => {
              const dateStr = fmt(addDays(t.week_start, i));
              return t.entries
                .filter((e) => e.date === dateStr)
                .reduce((s, e) => s + Number(e.hours || 0), 0);
            });
            return (
              <div
                key={t.id}
                className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden"
                data-testid={`approval-card-${t.id}`}
              >
                <div className="p-5 flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-base">{t.user_name}</div>
                    <div className="text-xs text-neutral-500">{t.user_email}</div>
                    <div className="text-xs text-neutral-500 mt-1 font-mono-num">
                      Week of {pretty(new Date(t.week_start))} —{" "}
                      {pretty(addDays(t.week_start, 6))}
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <Metric label="Hours" value={t.total_hours} />
                    <Metric label="Billable" value={t.billable_hours} />
                    <Metric label="Entries" value={t.entries.length} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm"
                      onClick={() => setExpanded(isOpen ? null : t.id)}
                      data-testid={`toggle-details-${t.id}`}
                    >
                      {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {isOpen ? "Hide" : "Details"}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="rounded-sm border-[#D82C0D] text-[#D82C0D] hover:bg-[#FCE8E6]"
                      onClick={() => setRejectTarget(t)}
                      data-testid={`reject-btn-${t.id}`}
                    >
                      <X size={14} className="mr-1" /> Reject
                    </Button>
                    <Button
                      size="sm"
                      className="rounded-sm bg-[#008060] hover:bg-[#006E52] text-white"
                      onClick={() => approve(t.id)}
                      data-testid={`approve-btn-${t.id}`}
                    >
                      <Check size={14} className="mr-1" /> Approve
                    </Button>
                  </div>
                </div>
                {isOpen && (
                  <div className="border-t border-[#E5E5E5] bg-[#F9F9F8] p-5">
                    <div className="grid grid-cols-7 gap-1 mb-4">
                      {dayTotals.map((h, i) => (
                        <div
                          key={i}
                          className="bg-white border border-[#E5E5E5] p-2 text-center"
                        >
                          <div className="text-[10px] uppercase tracking-widest text-neutral-500">
                            {DAY_NAMES[i]}
                          </div>
                          <div className="font-mono-num text-sm font-semibold mt-1">
                            {h.toFixed(2)}
                          </div>
                        </div>
                      ))}
                    </div>
                    <table className="w-full text-sm bg-white border border-[#E5E5E5]">
                      <thead className="bg-[#F3F3F2]">
                        <tr className="text-left text-xs uppercase tracking-wider text-neutral-500">
                          <th className="px-3 py-2">Date</th>
                          <th className="px-3 py-2">Project</th>
                          <th className="px-3 py-2">Description</th>
                          <th className="px-3 py-2">Daily note</th>
                          <th className="px-3 py-2">Billable</th>
                          <th className="px-3 py-2 text-right">Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {t.entries.map((e, idx) => (
                          <tr key={idx} className="border-t border-[#E5E5E5] align-top">
                            <td className="px-3 py-2 font-mono-num whitespace-nowrap">{e.date}</td>
                            <td className="px-3 py-2">{projects[e.project_id] || "—"}</td>
                            <td className="px-3 py-2 text-neutral-600">
                              {e.description || "—"}
                            </td>
                            <td className="px-3 py-2 text-neutral-700 whitespace-pre-wrap max-w-xs" data-testid={`entry-note-${t.id}-${idx}`}>
                              {e.note ? e.note : <span className="text-neutral-400">—</span>}
                            </td>
                            <td className="px-3 py-2">
                              {e.billable ? (
                                <span className="text-[#008060] text-xs">Yes</span>
                              ) : (
                                <span className="text-neutral-500 text-xs">No</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num">{e.hours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!rejectTarget} onOpenChange={(o) => !o && setRejectTarget(null)}>
        <DialogContent className="rounded-md">
          <DialogHeader>
            <DialogTitle className="font-display">Reject timesheet</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-600">
            Rejecting <strong>{rejectTarget?.user_name}</strong>&apos;s week of{" "}
            <span className="font-mono-num">{rejectTarget?.week_start}</span>.
          </div>
          <Textarea
            placeholder="Reason for rejection…"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            data-testid="reject-reason-input"
            className="rounded-sm"
            rows={4}
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectTarget(null)}
              data-testid="cancel-reject-btn"
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={reject}
              className="rounded-sm bg-[#D82C0D] hover:bg-[#B32309] text-white"
              data-testid="confirm-reject-btn"
            >
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-mono-num text-lg font-semibold">{value}</div>
    </div>
  );
}
